import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, message } from 'antd';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import { AzureDevOpsService, WorkItemWithTimeLog } from '../services/AzureDevOpsService';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';

interface EditFieldsModalProps {
  visible: boolean;
  workItem: WorkItemWithTimeLog | null;
  onClose: () => void;
  onUpdate: (workItemId: number, updatedFields: any) => void;
  capexOptions: string[];
  govProjectOptions: string[];
  priorityDriverOptions: string[];
  initiativeOptions: string[];
  productOptions: string[];
  investmentClassOptions: string[];
}

const EditFieldsModal: React.FC<EditFieldsModalProps> = ({
  visible,
  workItem,
  onClose,
  onUpdate,
  capexOptions,
  govProjectOptions,
  priorityDriverOptions,
  initiativeOptions,
  productOptions,
  investmentClassOptions
}) => {
  const [loading, setLoading] = useState(false);
  const [govProject, setGovProject] = useState<string>('');
  const [capex, setCapex] = useState<string>('');
  const [priorityDriver, setPriorityDriver] = useState<string>('');
  const [initiative, setInitiative] = useState<string>('');
  const [product, setProduct] = useState<string>('');
  const [investmentClass, setInvestmentClass] = useState<string>('');
  const { accounts, instance } = useMsal();

  useEffect(() => {
    if (visible && workItem) {
      setGovProject(workItem.inheritedFields?.govProject?.value || workItem.workItem.fields['Custom.GovProject'] || '');
      setCapex(workItem.inheritedFields?.capex?.value || workItem.workItem.fields['Custom.CapEx'] || '');
      setPriorityDriver(workItem.inheritedFields?.priorityDriver?.value || workItem.workItem.fields['Custom.PriorityDriver'] || '');
      setInitiative(workItem.workItem.fields['Custom.Initiative'] || '');
      setProduct(workItem.inheritedFields?.product?.value || workItem.workItem.fields['Custom.SensioProduct'] || '');
      setInvestmentClass(workItem.inheritedFields?.investmentClass?.value || workItem.workItem.fields['Custom.Investmentclass'] || '');
    }
  }, [visible, workItem]);

  const handleSave = async () => {
    if (!workItem || accounts.length === 0) return;

    try {
      setLoading(true);
      
      const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
      
      const fieldsToUpdate: any = {};
      
      if (govProject !== (workItem.workItem.fields['Custom.GovProject'] || '')) {
        fieldsToUpdate['Custom.GovProject'] = govProject || '';
      }
      
      if (capex !== (workItem.workItem.fields['Custom.CapEx'] || '')) {
        fieldsToUpdate['Custom.CapEx'] = capex || '';
      }
      
      if (priorityDriver !== (workItem.workItem.fields['Custom.PriorityDriver'] || '')) {
        fieldsToUpdate['Custom.PriorityDriver'] = priorityDriver || '';
      }
      
      if (initiative !== (workItem.workItem.fields['Custom.Initiative'] || '')) {
        fieldsToUpdate['Custom.Initiative'] = initiative || '';
      }

      if (product !== (workItem.workItem.fields['Custom.SensioProduct'] || '')) {
        fieldsToUpdate['Custom.SensioProduct'] = product || '';
      }

      if (investmentClass !== (workItem.workItem.fields['Custom.Investmentclass'] || '')) {
        fieldsToUpdate['Custom.Investmentclass'] = investmentClass || '';
      }

      if (Object.keys(fieldsToUpdate).length > 0) {
        const organization = 'sensio';
        const project = workItem.workItem.fields['System.TeamProject'];
        await devOpsService.updateTicketFields(organization, project, workItem.workItem.id, fieldsToUpdate);
        onUpdate(workItem.workItem.id, fieldsToUpdate);
        message.success('Fields updated successfully');
      } else {
        message.info('No changes to save');
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating work item fields:', error);
      message.error('Failed to update fields. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setGovProject('');
    setCapex('');
    setPriorityDriver('');
    setInitiative('');
    setProduct('');
    setInvestmentClass('');
    onClose();
  };

  return (
    <Modal
      title={
        <div>
          <EditOutlined /> Edit Fields - Work Item {workItem?.workItem.id}
        </div>
      }
      visible={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
        >
          <SaveOutlined /> Save Changes
        </Button>
      ]}
      width={500}
    >
      {workItem && (
        <div>
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <strong>{workItem.workItem.fields['System.Title']}</strong>
            <br />
            <small>
              {workItem.workItem.fields['System.WorkItemType']} • {workItem.workItem.fields['System.TeamProject']}
            </small>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Priority Driver
              </label>
              {workItem.inheritedFields?.priorityDriver?.inherited && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Currently inherited: "{workItem.inheritedFields.priorityDriver.value}"
                </div>
              )}
              <Select
                value={priorityDriver || undefined}
                placeholder="Select Priority Driver"
                allowClear
                showSearch
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setPriorityDriver(value || '')}
              >
                {priorityDriverOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Gov Project
              </label>
              {workItem.inheritedFields?.govProject?.inherited && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Currently inherited: "{workItem.inheritedFields.govProject.value}"
                </div>
              )}
              <Select
                value={govProject || undefined}
                placeholder="Select Gov Project"
                allowClear
                showSearch
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setGovProject(value || '')}
              >
                {govProjectOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                CapEx
              </label>
              {workItem.inheritedFields?.capex?.inherited && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Currently inherited: "{workItem.inheritedFields.capex.value}"
                </div>
              )}
              <Select
                value={capex || undefined}
                placeholder="Select CapEx"
                allowClear
                showSearch
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setCapex(value || '')}
              >
                {capexOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Initiative
              </label>
              <Select
                value={initiative || undefined}
                placeholder="Select Initiative"
                allowClear
                showSearch
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setInitiative(value || '')}
              >
                {initiativeOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Product
              </label>
              {workItem.inheritedFields?.product?.inherited && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Currently inherited: "{workItem.inheritedFields.product.value}"
                </div>
              )}
              <Select
                value={product || undefined}
                placeholder="Select Product"
                allowClear
                showSearch
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setProduct(value || '')}
              >
                {productOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Investment Class
              </label>
              {workItem.inheritedFields?.investmentClass?.inherited && (
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                  Currently inherited: "{workItem.inheritedFields.investmentClass.value}"
                </div>
              )}
              <Select
                value={investmentClass || undefined}
                placeholder="Select Investment Class"
                allowClear
                showSearch
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => setInvestmentClass(value || '')}
              >
                {investmentClassOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          <div style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
            <strong>Note:</strong> Setting a value here will override any inherited values from parent work items. Initiative field does not support inheritance.
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditFieldsModal;
